// Supplier portal home (Lane 1331; new look Lane 1394; one section per buyer, Lane 1442).
// One supplier person may quote for several Wingman customers: each buyer has its own
// section with its mark, its RFPs and its messages. Each section loads its own data
// (events and notices of that buyer only, by membership), so nothing mixes buyers.
// One line per table cell: a buyer's new messages show as a count in their RFP's row.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalShell, { BuyerMark, usePortalSession } from './PortalShell';
import { portalApi } from './portalApi';
import { EventStatus, OWN_STATUS_TEXT, formatDueAt, timeLeftText, zoneName } from './portalView';

const OWN_TONE = { submitted: 'srcp-pill--ok', viewed: 'srcp-pill--info', declined: '', not_started: 'srcp-pill--line' };

function sortNotices(items) {
  return [...items].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
}

// Open RFPs first (soonest due first), then the rest, newest due first.
function sortEvents(items) {
  const at = (e) => (e.due_at ? new Date(e.due_at).getTime() : Infinity);
  return [...items].sort((a, b) => {
    const ao = a.status === 'open' ? 0 : 1;
    const bo = b.status === 'open' ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return ao === 0 ? at(a) - at(b) : at(b) - at(a);
  });
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function Messages({ buyerName, notices, onRead }) {
  const unread = notices.filter((n) => !n.read).length;
  if (notices.length === 0) return null;
  return (
    <details className="srcp-buyer-msgs" open={unread > 0}>
      <summary>
        Messages from {buyerName}: {notices.length.toLocaleString()}{unread ? `, ${unread.toLocaleString()} new` : ''}
      </summary>
      <div className="srcp-scroll">
        <table className="srcp-grid">
          <thead><tr><th>Date</th><th>Subject</th><th>Message</th><th>Status</th><th><span className="srcp-sr">Actions</span></th></tr></thead>
          <tbody>
            {notices.map((n) => (
              <tr key={n.notice_id} className={n.read ? undefined : 'srcp-unread'}>
                <td>{formatDueAt(n.created_at)}</td>
                <td className={`srcp-ell${n.read ? '' : ' srcp-strong'}`} title={n.title}>{n.title}</td>
                <td className="srcp-ell srcp-ell--wide srcp-notes" title={n.body || ''}>{n.body || ''}</td>
                <td><span className={`srcp-pill${n.read ? '' : ' srcp-pill--info'}`}>{n.read ? 'Read' : 'New'}</span></td>
                <td className="srcp-r">
                  <span className="srcp-row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                    {!n.read && (
                      <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => onRead(n.notice_id)}>Mark read</button>
                    )}
                    {n.event_id && (
                      <Link className="srcp-btn srcp-btn--sm" to={`/sourcing-portal/events/${encodeURIComponent(n.event_id)}`}>Open the RFP</Link>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function BuyerSection({ buyer }) {
  const navigate = useNavigate();
  const membershipId = buyer.membership_id;
  const [events, setEvents] = useState(null);
  const [notices, setNotices] = useState(null);
  const [error, setError] = useState('');

  const loadNotices = useCallback(() => portalApi.notices(membershipId)
    .then((r) => setNotices(sortNotices(r.items || []))), [membershipId]);

  useEffect(() => {
    let live = true;
    Promise.all([portalApi.events(membershipId), portalApi.notices(membershipId)])
      .then(([ev, no]) => {
        if (!live) return;
        setEvents(sortEvents(ev.items || []));
        setNotices(sortNotices(no.items || []));
      })
      .catch((e) => { if (live && e.status !== 401) setError(e.message); });
    return () => { live = false; };
  }, [membershipId]);

  const markRead = async (noticeId) => {
    try {
      await portalApi.readNotice(noticeId);
      await loadNotices();
    } catch (e) {
      if (e.status !== 401) setError(e.message);
    }
  };

  const now = Date.now();
  const open = (events || []).filter((e) => e.status === 'open');
  const newFor = (eventId) => (notices || []).filter((n) => !n.read && n.event_id === eventId).length;
  const openEvent = (id) => navigate(`/sourcing-portal/events/${encodeURIComponent(id)}`);
  return (
    <section className="srcp-card srcp-buyer" aria-label={`RFPs from ${buyer.buyer_name}`}>
      <div className="srcp-buyer-h">
        <BuyerMark name={buyer.buyer_name} size={34} />
        <span className="srcp-buyer-name">{buyer.buyer_name}</span>
        <span className="srcp-mut">Buyer</span>
        <span className="srcp-grow" />
        {events && <span className="srcp-mut">{plural(open.length, 'open RFP')}</span>}
      </div>
      {error && <div className="srcp-card-b"><div className="srcp-error" role="alert">{error}</div></div>}
      {!error && events === null && <div className="srcp-card-b srcp-loading">Loading RFPs…</div>}
      {events && events.length === 0 && <div className="srcp-card-b srcp-mut">No RFPs from {buyer.buyer_name} yet.</div>}
      {events && events.length > 0 && (
        <div className="srcp-scroll">
          <table className="srcp-grid">
            <thead>
              <tr>
                <th>RFP</th><th>Due</th><th>Time left</th><th className="srcp-r">Round</th>
                <th>RFP status</th><th>Your quote</th><th>Messages</th><th><span className="srcp-sr">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const fresh = newFor(e.event_id);
                return (
                  <tr key={e.event_id} className="srcp-click" onClick={() => openEvent(e.event_id)}>
                    <td className="srcp-ell srcp-ell--wide" title={e.title}>
                      <Link className="srcp-strong srcp-rowlink" to={`/sourcing-portal/events/${encodeURIComponent(e.event_id)}`}
                        onClick={(ev) => ev.stopPropagation()}>{e.title}</Link>
                    </td>
                    <td>{formatDueAt(e.due_at) || 'No due time set'}</td>
                    <td>{e.status === 'open' ? timeLeftText(e.due_at, now) : ''}</td>
                    <td className="srcp-r srcp-num">{e.round || ''}</td>
                    <td><EventStatus status={e.status} /></td>
                    <td><span className={`srcp-pill${OWN_TONE[e.own_status] ? ` ${OWN_TONE[e.own_status]}` : ''}`}>
                      {OWN_STATUS_TEXT[e.own_status] || e.own_status}</span></td>
                    <td>{fresh ? <span className="srcp-pill srcp-pill--info">{fresh} new</span> : ''}</td>
                    <td className="srcp-r">
                      <button type="button" className={`srcp-btn srcp-btn--sm${e.status === 'open' ? ' srcp-btn--primary' : ''}`}
                        onClick={(ev) => { ev.stopPropagation(); openEvent(e.event_id); }}>
                        {e.status === 'open' ? 'Open' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {notices && <Messages buyerName={buyer.buyer_name} notices={notices} onRead={markRead} />}
    </section>
  );
}

function Home() {
  const { me } = usePortalSession();
  const buyers = me.buyers || [];
  if (buyers.length === 0) {
    return (
      <div className="srcp-empty">
        <div className="srcp-strong">No buyers yet</div>
        Open the invitation link a buyer sent you to join an RFP.
      </div>
    );
  }
  return (
    <>
      <div className="srcp-titlerow">
        <div className="srcp-titlegrow"><h1 className="srcp-h1">Your RFPs</h1></div>
        {zoneName() && <span className="srcp-mut">Times show in your time zone: {zoneName()}</span>}
      </div>
      {buyers.map((b) => <BuyerSection key={b.membership_id} buyer={b} />)}
    </>
  );
}

export default function HomePage() {
  return <PortalShell><Home /></PortalShell>;
}
